import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { useInfiniteQuery } from "@tanstack/react-query";
import { History } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { catColor } from "@/lib/lore";
import { PageHeader } from "@/components/CategoryBadge";

const day = (iso) => new Date(iso).toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" });
const time = (iso) => new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });

const Row = ({ a, onOpen }) => {
  const name = a.entity?.name;
  const i = name ? a.label.lastIndexOf(name) : -1;
  return (
    <li className="flex gap-3 py-2.5" data-testid={`activity-${a.id}`}>
      <span className="w-12 shrink-0 pt-0.5 font-mono-lf text-[11px] text-[#A8A090]">{time(a.created_at)}</span>
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#D97706]/20 text-[11px] font-bold text-[#F59E0B]">{a.by_name?.[0]?.toUpperCase()}</span>
      <p className="min-w-0 text-sm text-[#E8DFD1]">
        <span className="font-medium text-[#F3EBDD]">{a.by_name}</span>{" "}
        {i >= 0 ? (
          <>{a.label.slice(0, i)}<button onClick={() => onOpen(a.entity.id)} className="underline decoration-dotted underline-offset-4 hover:text-[#F59E0B]" style={{ textDecorationColor: catColor(a.entity.category) }} data-testid={`activity-entity-${a.id}`}>{name}</button>{a.label.slice(i + name.length)}</>
        ) : a.label}
      </p>
    </li>
  );
};

export default function ActivityPage() {
  const { universe, openEntity } = useOutletContext();
  const [by, setBy] = useState(null);
  const q = useInfiniteQuery({
    queryKey: ["activity", universe.id, by],
    queryFn: ({ pageParam }) => api.get(`/universes/${universe.id}/activity`, { params: { ...(by ? { by } : {}), ...(pageParam ? { before: pageParam } : {}) } }).then((r) => r.data),
    initialPageParam: null,
    getNextPageParam: (last) => (last.length === 100 ? last[last.length - 1].created_at : undefined),
  });
  const items = q.data?.pages.flat() || [];
  const people = [...new Map(items.map((a) => [a.by_id, a.by_name])).entries()];
  const groups = items.reduce((acc, a) => { const d = day(a.created_at); (acc[d] = acc[d] || []).push(a); return acc; }, {});

  return (
    <div className="px-4 py-10 sm:px-8 lg:px-12" data-testid="activity-page">
      <PageHeader eyebrow="Who changed what" title="Edits log" />
      <div className="mb-8 flex flex-wrap gap-1.5" data-testid="activity-people">
        <button onClick={() => setBy(null)} className={`rounded-full border px-3 py-1 text-xs ${!by ? "border-[#D97706] text-[#F59E0B]" : "border-[#2D3442] text-[#A8A090]"}`} data-testid="activity-filter-all">Everyone</button>
        {people.map(([id, n]) => (
          <button key={id} onClick={() => setBy(id)} className={`rounded-full border px-3 py-1 text-xs ${by === id ? "border-[#D97706] text-[#F59E0B]" : "border-[#2D3442] text-[#A8A090]"}`} data-testid={`activity-filter-${id}`}>{n}</button>
        ))}
      </div>
      {q.isLoading ? <p className="text-[#A8A090]">Loading…</p> : items.length === 0 ? (
        <div className="rounded-md border border-dashed border-[#2D3442] p-10" data-testid="activity-empty">
          <History className="mb-3 h-6 w-6 text-[#A8A090]" />
          <p className="font-display text-3xl text-[#F3EBDD]">No edits logged yet.</p>
          <p className="mt-2 text-sm text-[#A8A090]">Changes made from now on appear here with who made them and when.</p>
        </div>
      ) : (
        <div className="max-w-3xl space-y-8">
          {Object.entries(groups).map(([d, list]) => (
            <section key={d}>
              <div className="lf-label mb-1 border-b border-[#2D3442] pb-2">{d}</div>
              <ul className="divide-y divide-[#2D3442]/50">{list.map((a) => <Row key={a.id} a={a} onOpen={openEntity} />)}</ul>
            </section>
          ))}
          {q.hasNextPage && <Button variant="outline" onClick={() => q.fetchNextPage()} disabled={q.isFetchingNextPage} data-testid="activity-load-more">{q.isFetchingNextPage ? "Loading…" : "Load older"}</Button>}
        </div>
      )}
    </div>
  );
}
